/* import __COLOCATED_TEMPLATE__ from './add-followers-modal.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { task, dropTask } from 'ember-concurrency';
import { closeModal} from 'sh-www/utils/modal-controls';

class AddFollowersModalComponent extends Component {
  @service('state-managers/application') applicationStateManager;

  @tracked newUsersSelected = [];
  @tracked newUsersDeselected = [];
  @tracked potentialFollowers = [];
  @tracked currentFollowers = [];

  #listItems = createCache(() => {
    if (this.loadPotentialFollowers.isRunning || this.loadCurrentFollowers.isRunning) {
      return [];
    }
    
    const listItems = [];

    const isWatcherMap = this.currentFollowers.reduce((watcherMap, { id }) => {
      watcherMap[id] = true;
      return watcherMap;
    }, {});

    // These are the users that have been selected to follow, but have not been updated on the server yet.
    this.newUsersSelected.forEach((userIdSelected) => {
      isWatcherMap[userIdSelected] = true;
    });

    // These are the users that have been selected to UNfollow, but have not been updated on the server yet.
    this.newUsersDeselected.forEach(userIdSelected => {
      isWatcherMap[userIdSelected] = false;
    });

    this.potentialFollowers.forEach(employee => {
      listItems.push({
        userImageUri: employee.image.transforms.profile128,
        userId: employee.id,
        userName: employee.fullName,
        isSelected: !!isWatcherMap[employee.id]
      });
    });

    return listItems.sort((usernameA, usernameB) => usernameA.userName.localeCompare(usernameB.userName, 'en', { sensitivity: 'base' }));
  });

  /**
   * [{
   *    userImageUri: 'https://cloudinary.com/sjeifjei',
   *    userName: 'Brian bBBB',
   *    isSelected: true
   * }]
   *
   * @type {Ember.MutableArray}
   */
  get listItems() { 
    return getValue(this.#listItems); 
  }

  /** Fetches user and followers lists for modal */
  @dropTask
  *_initializeModels() {
    const pending = [];

    // Check if the currentFollowers array was passed in, otherwise perform the task to fetch them
    if(this.args.model.currentFollowers) {
      this.currentFollowers = this.args.model.currentFollowers;
    } else {
      pending.push(this.loadCurrentFollowers.perform());
    }

    pending.push(this.loadPotentialFollowers.perform());
    yield Promise.all(pending);
  }

  @task
  *loadPotentialFollowers() {
    const { companyId, subject, loadPotentialFollowersTask } = this.args.model;
    this.potentialFollowers = yield loadPotentialFollowersTask.linked().perform(companyId, subject);
  }

  @task
  *loadCurrentFollowers() {
    const { subject, loadCurrentFollowersTask} = this.args.model;
    this.currentFollowers = yield loadCurrentFollowersTask.linked().perform(subject.id);
  }

  @task
  *saveFollowers() {
    const { subject, saveFollowersTask } = this.args.model;
    yield saveFollowersTask.unlinked().perform(this.newUsersSelected, this.newUsersDeselected, subject.id);
  }

  /**
   * Called when the user has been selected in the list.
   * @param {String} userId
   */
  @action
  onUserSelected(userId) {
    if (this.newUsersDeselected.includes(userId)) {
      this.newUsersDeselected = this.newUsersDeselected.filter(deselectedId => {
        return deselectedId !== userId;
      });
    } else {
      this.newUsersSelected = [...this.newUsersSelected, userId];
    }
  }

  /**
   * Called when user deselected in list.
   * @param {String} userId
   */
  @action
  onUserDeselected(userId) {
    if (this.newUsersSelected.includes(userId)) {
      this.newUsersSelected = this.newUsersSelected.filter(selectedId => {
        return selectedId !== userId;
      });
    } else {
      this.newUsersDeselected = [...this.newUsersDeselected, userId];
    }
  }

  @action
  onConfirmAction() {
    this.saveFollowers.perform();
    closeModal(this);
  }

  @action
  onCancelAction() {
    closeModal(this);
  }
}

export default AddFollowersModalComponent;
